import React from 'react';
import { Box, Card, CardContent, Typography, styled, Grid } from '@mui/material';
import { GenomicReportMetrics } from '../../types/genomic';
import SpeedIcon from '@mui/icons-material/Speed';
import StorageIcon from '@mui/icons-material/Storage';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DnsIcon from '@mui/icons-material/Dns';
import DataThresholdingIcon from '@mui/icons-material/DataThresholding';

interface QueryMetricsCardProps {
  metrics: GenomicReportMetrics;
  conditionsCount: number;
}

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  boxShadow: 'none',
  backgroundColor: theme.palette.background.default,
  pageBreakInside: 'avoid',
}));

const MetricBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.divider}`,
  height: '100%',
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  fontSize: '1.25rem',
  marginTop: theme.spacing(0.5),
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  fontWeight: 400,
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const QueryMetricsCard: React.FC<QueryMetricsCardProps> = ({ metrics, conditionsCount }) => {
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(2)} seconds`;
  };

  const formatLargeNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return formatNumber(num);
  };

  return (
    <StyledCard raised={false}>
      <CardContent sx={{ padding: 1 }}>
        <HeaderBox>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Successfully analyzed {formatBytes(metrics.vcfBytesRead)} of genomic data in {formatTime(metrics.totalTimeMs)}
            </Typography>
          </Box>
          {/*<StatusChip
            icon={<CheckCircleOutlineIcon />}
            label="Complete"
            size="small"
          />*/}
        </HeaderBox>

        <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
            <MetricBox>
              <Box display="flex" alignItems="center" gap={1}>
                <DnsIcon color="primary" fontSize="small" />
                <MetricLabel>Considered Variants</MetricLabel>
              </Box>
              <MetricValue>{formatNumber(metrics.variantsReturned)}</MetricValue>
              <Typography variant="caption" color="text.secondary">
                Matching query criteria
              </Typography>
            </MetricBox>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <MetricBox>
              <Box display="flex" alignItems="center" gap={1}>
                <DataThresholdingIcon color="primary" fontSize="small" />
                <MetricLabel>Your DNA Analyzed</MetricLabel>
              </Box>
              <MetricValue>{formatNumber(metrics.variantsMatchedInVcf)}</MetricValue>
              <Typography variant="caption" color="text.secondary">
                Variants found in your DNA
              </Typography>
            </MetricBox>
          </Grid>

         

          <Grid item xs={12} sm={6} md={4}>
            <MetricBox>
              <Box display="flex" alignItems="center" gap={1}>
                <SpeedIcon color="primary" fontSize="small" />
                <MetricLabel>Analysis Speed</MetricLabel>
              </Box>
              <MetricValue>{formatLargeNumber(Math.round(metrics.variantsPerSecond))} variants/s</MetricValue>
              <Typography variant="caption" color="text.secondary">
                Rapid data processing
              </Typography>
            </MetricBox>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <MetricBox>
              <Box display="flex" alignItems="center" gap={1}>
                <AccessTimeIcon color="primary" fontSize="small" />
                <MetricLabel>Total Time</MetricLabel>
              </Box>
              <MetricValue>{formatTime(metrics.vcfReadTimeMs)}</MetricValue>
              <Typography variant="caption" color="text.secondary">
                Time taken to prioritize variants
              </Typography>
            </MetricBox>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <MetricBox>
              <Box display="flex" alignItems="center" gap={1}>
                <StorageIcon color="primary" fontSize="small" />
                <MetricLabel>Genomic Data</MetricLabel>
              </Box>
              <MetricValue>{formatBytes(metrics.vcfBytesRead)}</MetricValue>
              <Typography variant="caption" color="text.secondary">
                Genomic data examined
              </Typography>
            </MetricBox>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <MetricBox>
              <Box display="flex" alignItems="center" gap={1}>
                <DnsIcon color="primary" fontSize="small" />
                <MetricLabel>Conditions Examined</MetricLabel>
              </Box>
              <MetricValue>{conditionsCount}</MetricValue>
              <Typography variant="caption" color="text.secondary">
                Comprehensive condition analysis
              </Typography>
            </MetricBox>
          </Grid>
        </Grid>
      </CardContent>
    </StyledCard>
  );
};

export default QueryMetricsCard;
